import type { FC } from 'react'
import { useEffect } from 'react'

import { InfoBox } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import orderBy from 'lodash/orderBy'

import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'
import { useRenderLimit } from '@/hooks/use-render-limit'
import queries from '@/modules/ecom-policy/queries'
import ecomPolicyResources from '@/modules/ecom-policy/resources'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import type { CategoryData, ProductGroupData } from '@/modules/ecom-policy/types'

import Filter from '../Filter'
import ProductGroup from '../ProductGroup'
import ProgressButtons from '../ProgressButtons'

const StepProductsAccessories: FC<{
  orderSummary:
    | {
        cost: number
        products: number
        currency: string | null
      }
    | undefined
  availableAccessoryCategories: CategoryData[]
  groupedAndFilteredAccessories: Record<string, ProductGroupData>
  advanceWizard?: () => void
  regressWizard?: () => void
}> = ({
  orderSummary,
  availableAccessoryCategories,
  groupedAndFilteredAccessories,
  advanceWizard,
  regressWizard,
}) => {
  const shopCtx = useShopNewOrderContext()
  const [, setSearchQuery] = shopCtx.searchQuery
  const [, setSelectedCategoryIds] = shopCtx.selectedCategoryIds
  const [selectedPolicyId] = shopCtx.selectedPolicyId
  const [, setStockFilter] = shopCtx.stockFilter
  const [sortParam, setSortParam] = shopCtx.sortParam
  const [sortOrder, setSortOrder] = shopCtx.sortOrder
  const [, setActiveStepName] = shopCtx.activeStepName

  const { data: policy } = useQuery(
    selectedPolicyId ? queries.policyDetails(selectedPolicyId) : [''],
    () => {
      if (!selectedPolicyId) throw Error('No policy selected!')
      return ecomPolicyResources.getPolicyDetails(selectedPolicyId)
    }
  )

  useEffect(() => {
    setActiveStepName('ACCESSORIES')
  }, [setActiveStepName])

  useEffect(
    // Reset filters when leaving this step
    () => () => {
      setStockFilter(false)
      setSortParam(undefined)
      setSortOrder('asc')
      setSelectedCategoryIds(null)
      setSearchQuery('')
    },
    [setSearchQuery, setSelectedCategoryIds, setSortOrder, setSortParam, setStockFilter]
  )

  const sortedInventoryList = orderBy(groupedAndFilteredAccessories, sortParam, sortOrder)
  const { visibleList, ListLoaderComponent } = useRenderLimit(
    Object.values(sortedInventoryList) || [],
    24
  )

  const { recommendedAccessories, otherAccessories } = visibleList.reduce(
    (
      acc: {
        recommendedAccessories: ProductGroupData[]
        otherAccessories: ProductGroupData[]
      },
      accessory
    ) => {
      if (accessory.isRecommended) {
        acc.recommendedAccessories.push(accessory)
        return acc
      }

      acc.otherAccessories.push(accessory)
      return acc
    },
    { recommendedAccessories: [], otherAccessories: [] }
  )

  const isRecommendedAccessoriesVisible = recommendedAccessories.length > 0
  const isOtherAccessoriesVisible = otherAccessories.length > 0
  const isBothGroupsVisible = isRecommendedAccessoriesVisible && isOtherAccessoriesVisible

  return (
    <div>
      <Filter availableCategories={availableAccessoryCategories || []} />

      {policy?.stepAccessoriesDescription && (
        <InfoBox emphasis="warning" marginBottom={16} marginTop={16}>
          <h3 className="m-0 whitespace-pre-wrap text-sm font-normal">
            {policy.stepAccessoriesDescription}
          </h3>
        </InfoBox>
      )}

      {isRecommendedAccessoriesVisible && (
        <>
          {isBothGroupsVisible && (
            <h3>
              <Translation id="Recommended accessories" />
            </h3>
          )}

          <div className="inventory__products">
            {recommendedAccessories.map(group => (
              <ProductGroup key={group.groupKey} {...group} />
            ))}
          </div>
        </>
      )}

      {isOtherAccessoriesVisible && (
        <>
          {isBothGroupsVisible && (
            <h3>
              <Translation id="Other accessories" />
            </h3>
          )}

          <div className="inventory__products">
            {otherAccessories.map(group => (
              <ProductGroup key={group.groupKey} {...group} />
            ))}
          </div>
        </>
      )}

      {visibleList.length === 0 && (
        <NoResults>
          <Translation id="No products..." />
        </NoResults>
      )}
      <ListLoaderComponent />

      <ProgressButtons
        orderSummary={orderSummary}
        advanceWizard={advanceWizard}
        regressWizard={regressWizard}
      />
    </div>
  )
}

export default StepProductsAccessories
